import pino from 'pino'

// Configure log levels
const levels = {
    debug: 10,
    info: 20,
    http: 30,
    warn: 40,
    error: 50,
    fatal: 60,
    success: 70,
}

interface ILogType {
    context: string
    symbol: string
    args?: unknown[]
}

// Get the current time in the format "HH:mm:ss.SSS"
const getCurrentTime = (): string => {
    const now = new Date()
    return now.toLocaleTimeString('en-US', { hour12: false }) + `.${now.getMilliseconds().toString().padStart(3, '0')}`
}

// Browser-friendly logger configuration
export const logger = pino({
    level: process.env.PINO_LOG_LEVEL || (process.env.NODE_ENV === 'production' ? 'info' : 'debug'),
    customLevels: levels,
    browser: {
        write: {
            info: o => {
                const logArr = (o as ILogType).args
                if (Array.isArray(logArr))
                    console.log(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, ...logArr)
                else console.log(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, logArr)
            },
            warn: o => {
                const logArr = (o as ILogType).args
                if (Array.isArray(logArr))
                    console.warn(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, ...logArr)
                else console.warn(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, logArr)
            },
            error: o => {
                const logArr = (o as ILogType).args
                if (Array.isArray(logArr))
                    console.error(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, ...logArr)
                else console.error(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, logArr)
            },
            debug: o => {
                const logArr = (o as ILogType).args
                if (Array.isArray(logArr))
                    console.debug(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, ...logArr)
                else console.debug(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, logArr)
            },
            fatal: o => {
                const logArr = (o as ILogType).args
                if (Array.isArray(logArr))
                    console.error(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, ...logArr)
                else console.error(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, logArr)
            },
            http: o => {
                const logArr = (o as ILogType).args
                if (Array.isArray(logArr))
                    console.info(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, ...logArr)
                else console.info(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, logArr)
            },
            success: o => {
                const logArr = (o as ILogType).args
                if (Array.isArray(logArr))
                    console.log(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, ...logArr)
                else console.log(`[${(o as ILogType).context}] ${(o as ILogType).symbol}`, logArr)
            },
        },
    },
})

// Create context loggers for different parts of the application
export const createContextLogger = (context: string) => ({
    http: (...args: unknown[]) =>
        logger.info({
            context: `${context} ${getCurrentTime()}`,
            symbol: '🌐',
            args,
        }),
    debug: (...args: unknown[]) =>
        logger.debug({
            context: `${context} ${getCurrentTime()}`,
            symbol: '🔍',
            args,
        }),
    info: (...args: unknown[]) =>
        logger.info({
            context: `${context} ${getCurrentTime()}`,
            symbol: 'ℹ️',
            args,
        }),
    warn: (...args: unknown[]) =>
        logger.warn({
            context: `${context} ${getCurrentTime()}`,
            symbol: '⚠️',
            args,
        }),
    error: (...args: unknown[]) =>
        logger.error({
            context: `${context} ${getCurrentTime()}`,
            symbol: '❌',
            args,
        }),
    fatal: (...args: unknown[]) =>
        logger.fatal({
            context: `${context} ${getCurrentTime()}`,
            symbol: '💀',
            args,
        }),
    success: (...args: unknown[]) =>
        logger.success({
            context: `${context} ${getCurrentTime()}`,
            symbol: '✅',
            args,
        }),
})

export const exchangeLogger = createContextLogger('Exchange')
export const solanaLogger = createContextLogger('Solana')
export const tronLogger = createContextLogger('Tron')
export const walletLogger = createContextLogger('Wallet')
export const gasFeeslogger = createContextLogger('approximateGasFees')
